import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Plus, Trash2, Pencil, Eye, EyeOff, Loader2, ImagePlus, X, ExternalLink,
} from "lucide-react";
import { api, authHeaders, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BRAND } from "@/lib/data";

const emptyForm = {
  title: "", subtitle: "", button_text: "", button_link: "", order: 0, active: true,
  start_at: "", end_at: "",
};

const scheduleStatus = (b) => {
  const today = new Date().toISOString().slice(0, 10);
  if (b.start_at && today < b.start_at) return { label: "Scheduled", cls: "bg-amber-100 text-amber-700" };
  if (b.end_at && today > b.end_at) return { label: "Expired", cls: "bg-red-100 text-red-700" };
  return null;
};

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/banners", { headers: authHeaders() });
      setBanners(data);
    } catch {
      toast.error("Could not load banners");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFile(null);
    setPreview(null);
  };

  const startEdit = (b) => {
    setEditingId(b.id);
    setForm({
      title: b.title, subtitle: b.subtitle, button_text: b.button_text,
      button_link: b.button_link, order: b.order, active: b.active,
      start_at: b.start_at || "", end_at: b.end_at || "",
    });
    setFile(null);
    setPreview(fileUrl(b.image_path));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editingId && !file) {
      toast.error("Please choose a banner image");
      return;
    }
    setSaving(true);
    const fd = new FormData();
    fd.append("title", form.title);
    fd.append("subtitle", form.subtitle);
    fd.append("button_text", form.button_text);
    fd.append("button_link", form.button_link);
    fd.append("order", String(form.order || 0));
    fd.append("active", String(form.active));
    fd.append("start_at", form.start_at || "");
    fd.append("end_at", form.end_at || "");
    if (file) fd.append("image", file);
    try {
      if (editingId) {
        await api.put(`/admin/banners/${editingId}`, fd, { headers: authHeaders() });
        toast.success("Banner updated");
      } else {
        await api.post("/admin/banners", fd, { headers: authHeaders() });
        toast.success("Banner created");
      }
      resetForm();
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    try {
      await api.patch(`/admin/banners/${id}/toggle`, {}, { headers: authHeaders() });
      load();
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this banner?")) return;
    try {
      await api.delete(`/admin/banners/${id}`, { headers: authHeaders() });
      toast.success("Banner deleted");
      if (editingId === id) resetForm();
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  const inputCls =
    "w-full bg-transparent border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground/70 focus:border-accent focus:outline-none transition-colors duration-300";

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-display text-2xl tracking-tight">{BRAND.name}</span>
            <span className="font-mono-label text-[0.6rem] uppercase tracking-widest text-accent border-l border-border pl-3">
              Banner Admin
            </span>
          </div>
          <div className="flex items-center gap-5">
            <a href="/" target="_blank" rel="noreferrer" className="hidden sm:inline-flex items-center gap-2 text-xs font-mono-label uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors" data-testid="view-site">
              View Site <ExternalLink size={13} />
            </a>
            <span className="hidden sm:block text-sm text-muted-foreground">{admin?.email}</span>
            <button onClick={doLogout} className="inline-flex items-center gap-2 text-xs font-mono-label uppercase tracking-widest hover:text-accent transition-colors" data-testid="logout-btn">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-10">
        {/* Form */}
        <div className="lg:col-span-2">
          <div className="bg-white border border-border p-7 lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-2xl">
                {editingId ? "Edit banner" : "New banner"}
              </h2>
              {editingId && (
                <button onClick={resetForm} className="text-muted-foreground hover:text-accent" data-testid="cancel-edit">
                  <X size={18} />
                </button>
              )}
            </div>
            <form onSubmit={submit} className="space-y-4" data-testid="banner-form">
              <label className="block">
                <span className="font-mono-label text-[0.6rem] uppercase tracking-widest text-muted-foreground">Image {editingId ? "(leave to keep)" : "*"}</span>
                <div className="mt-2 relative border border-dashed border-border aspect-[16/6] overflow-hidden flex items-center justify-center bg-secondary/50 cursor-pointer">
                  {preview ? (
                    <img src={preview} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <span className="flex flex-col items-center text-muted-foreground">
                      <ImagePlus size={26} />
                      <span className="text-xs mt-2">Choose image</span>
                    </span>
                  )}
                  <input type="file" accept="image/*" onChange={pickFile} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="banner-image-input" />
                </div>
              </label>
              <input className={inputCls} placeholder="Title" value={form.title} onChange={set("title")} data-testid="banner-title" />
              <input className={inputCls} placeholder="Subtitle" value={form.subtitle} onChange={set("subtitle")} data-testid="banner-subtitle" />
              <div className="grid grid-cols-2 gap-4">
                <input className={inputCls} placeholder="Button text" value={form.button_text} onChange={set("button_text")} data-testid="banner-btn-text" />
                <input className={inputCls} placeholder="Button link (#services or URL)" value={form.button_link} onChange={set("button_link")} data-testid="banner-btn-link" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <label className="block">
                  <span className="font-mono-label text-[0.6rem] uppercase tracking-widest text-muted-foreground">Start date</span>
                  <input type="date" className={`${inputCls} mt-1.5`} value={form.start_at} onChange={set("start_at")} data-testid="banner-start" />
                </label>
                <label className="block">
                  <span className="font-mono-label text-[0.6rem] uppercase tracking-widest text-muted-foreground">End date</span>
                  <input type="date" className={`${inputCls} mt-1.5`} value={form.end_at} onChange={set("end_at")} data-testid="banner-end" />
                </label>
              </div>
              <p className="text-[0.7rem] text-muted-foreground -mt-1">Leave dates empty to show the banner immediately with no expiry.</p>
              <div className="flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <span className="font-mono-label text-[0.6rem] uppercase tracking-widest text-muted-foreground">Order</span>
                  <input type="number" className="w-20 border border-border px-3 py-2" value={form.order} onChange={set("order")} data-testid="banner-order" />
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={form.active} onChange={set("active")} className="w-4 h-4 accent-[hsl(358_74%_49%)]" data-testid="banner-active" />
                  <span>Active</span>
                </label>
              </div>
              <button type="submit" disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-foreground text-white py-3.5 text-xs font-mono-label uppercase tracking-widest hover:bg-accent transition-colors duration-300 disabled:opacity-60" data-testid="banner-submit">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />}
                {editingId ? "Update Banner" : "Add Banner"}
              </button>
            </form>
          </div>
        </div>

        {/* List */}
        <div className="lg:col-span-3">
          <h2 className="font-display text-3xl mb-6" data-testid="banners-heading">
            Banners <span className="text-muted-foreground text-xl">({banners.length})</span>
          </h2>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground"><Loader2 size={16} className="animate-spin" /> Loading...</div>
          ) : banners.length === 0 ? (
            <div className="border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="banners-empty">
              No banners yet. Create your first promotional banner.
            </div>
          ) : (
            <div className="space-y-4">
              {banners.map((b) => (
                <div key={b.id} className="bg-white border border-border overflow-hidden flex flex-col sm:flex-row" data-testid={`banner-row-${b.id}`}>
                  <div className="sm:w-48 aspect-[16/6] sm:aspect-auto shrink-0 bg-secondary">
                    <img src={fileUrl(b.image_path)} alt={b.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 p-5 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-xl leading-tight">{b.title || "Untitled"}</h3>
                        <span className={`text-[0.6rem] font-mono-label uppercase tracking-widest px-2 py-0.5 ${b.active ? "bg-green-100 text-green-700" : "bg-secondary text-muted-foreground"}`}>
                          {b.active ? "Active" : "Hidden"}
                        </span>
                        {scheduleStatus(b) && (
                          <span className={`text-[0.6rem] font-mono-label uppercase tracking-widest px-2 py-0.5 ${scheduleStatus(b).cls}`}>
                            {scheduleStatus(b).label}
                          </span>
                        )}
                      </div>
                      {b.subtitle && <p className="text-sm text-muted-foreground mt-1">{b.subtitle}</p>}
                      <p className="text-xs text-muted-foreground/70 mt-2 font-mono-label">
                        Order {b.order} · {b.button_text || "no button"}
                        {(b.start_at || b.end_at) && ` · ${b.start_at || "…"} → ${b.end_at || "…"}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 mt-4">
                      <button onClick={() => toggle(b.id)} className="inline-flex items-center gap-1.5 text-xs font-mono-label uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors" data-testid={`toggle-${b.id}`}>
                        {b.active ? <EyeOff size={14} /> : <Eye size={14} />} {b.active ? "Hide" : "Show"}
                      </button>
                      <button onClick={() => startEdit(b)} className="inline-flex items-center gap-1.5 text-xs font-mono-label uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors" data-testid={`edit-${b.id}`}>
                        <Pencil size={14} /> Edit
                      </button>
                      <button onClick={() => remove(b.id)} className="inline-flex items-center gap-1.5 text-xs font-mono-label uppercase tracking-widest text-muted-foreground hover:text-accent transition-colors" data-testid={`delete-${b.id}`}>
                        <Trash2 size={14} /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
